import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CLUB_FILTERS,
  MAX_PAGE,
  RESERVATION_SORTS,
  RESERVATION_STATUS_FILTERS,
  RESERVATION_TYPES,
  SITE_FILTER_LABEL,
  SITE_FILTERS,
  SOURCE_FILTERS,
  type ReservationRow,
  type ReservationSort,
} from '@smile/shared';
import { api } from '../api/client';
import {
  CLUB_FILTER_OPTIONS,
  CZK,
  LANGUAGE_FLAG,
  rezWord,
  SOURCE_FILTER_OPTIONS,
  SOURCE_LABEL,
  STATUS_CLASS,
  STATUS_FILTER_OPTIONS,
  STATUS_LABEL,
  TYPE_FILTER_LABEL,
  TYPE_SHORT,
  zaznamWord,
} from '../lib/format';
import {
  DATE_FIELDS,
  DATEFIELD_LABEL,
  type DateField,
  type Filters,
  isSearching,
  makeDefaults,
  pageList,
  parseFilters,
  QUICK_RANGES,
  quickRangeKey,
  SEARCH_DEBOUNCE_MS,
  toQuery,
  toSearchParams,
} from '../lib/filters';
import { Drawer, rowToggle } from '../components/RowDrawer';
import { ServiceIcon } from '../components/ServiceIcon';
import { Footer } from '../components/Footer';

/**
 * The Rezervace screen is the legacy `page.reservations.html` — its filter
 * box, its fourteen columns in their order, its footer — over our API.
 *
 * The owner's instruction on 30 September, after the two pages disagreed on
 * "reservations made yesterday": "the logic of our page is wrong, copy the
 * logic of the old table". So this file follows the old template, control for
 * control, and the query it sends follows page.reservations.php clause for
 * clause (buildReservationWhere). Where this screen had grown its own ideas —
 * a fortnight-ahead window on the arrival date, a search that switched the
 * date box off, "Ověřená" for a parked car — they are gone, because a
 * dispatcher reading our count against the old page's must get the same
 * number for the same filter.
 *
 * What is kept from ours, because the owner asked for it by name and the old
 * page never had it: the quick-range buttons beside the dates, the "Kde
 * stojí" yard filter, "Jen nedoplatky", "Chybí auto / SPZ", and the row that
 * opens under itself instead of navigating away.
 *
 * The filters live in the URL, not in component state: a filtered view is a
 * thing that can be kept, shared and reloaded. The parsing, the defaults and
 * the query construction are in ../lib/filters, which imports nothing and is
 * covered by `npm run verify:web`.
 */
const CATALOGUE = {
  statuses: RESERVATION_STATUS_FILTERS as readonly string[],
  sorts: RESERVATION_SORTS as readonly string[],
  // The API's own ceiling, quoted rather than copied.
  maxPage: MAX_PAGE,
  // The five yards plus 'none' — the same list SiteFilter's enum is built
  // from, so the control and the schema cannot offer different options.
  sites: SITE_FILTERS,
  clubs: CLUB_FILTERS,
  sources: SOURCE_FILTERS,
  types: RESERVATION_TYPES,
};

/** '2026-07-24 18:30:00' → { d: '24. 7. 2026', t: '18:30' } — the legacy 'j. n. Y' + 'H:i'. */
function splitFull(v: string | null): { d: string; t: string } {
  if (!v || v.length < 16 || v.startsWith('0000')) return { d: '—', t: '' };
  return {
    d: `${Number(v.slice(8, 10))}. ${Number(v.slice(5, 7))}. ${v.slice(0, 4)}`,
    t: v.slice(11, 16),
  };
}

/**
 * The legacy row tint (page.reservations.html's `<tr class>`): changed in
 * the last 31 minutes, deleted, or a car on the yard — in that precedence.
 */
function legacyRowClass(r: ReservationRow): string {
  if (r.recentUpdate) return 'recent';
  if (r.status === 'deleted') return 'deleted';
  if (r.status === 'verified') return 'parked';
  return '';
}

export function Rezervace() {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => parseFilters(sp, CATALOGUE), [sp]);
  const wrapRef = useRef<HTMLDivElement>(null);

  /* Filter changes replace rather than push, so the browser Back button
     steps out of the screen instead of unwinding thirty keystrokes — but
     Back from a reservation detail still lands on the exact same view. */
  const apply = (patch: Partial<Filters>) =>
    setSp(toSearchParams({ ...filters, ...patch, page: patch.page ?? 1 }), { replace: true });

  /* ---- free-text box, debounced against the URL ---- */
  const [term, setTerm] = useState(filters.q);
  const settled = useRef(filters.q);

  useEffect(() => {
    // Back/forward, or the reset button, changed q behind our back: follow.
    if (filters.q !== settled.current) {
      settled.current = filters.q;
      setTerm(filters.q);
    }
  }, [filters.q]);

  useEffect(() => {
    if (term === settled.current) return;
    // Debounced: a query per keystroke against 97k rows would be a storm on
    // a 2013-era MySQL box.
    const id = setTimeout(() => {
      settled.current = term;
      apply({ q: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const searching = isSearching(filters);
  const params = useMemo(() => toQuery(filters), [filters]);

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['reservations', params],
    queryFn: () => api.reservations(params),
    placeholderData: keepPreviousData,
    // The real legacy refresh cadence is an idle check, not a 5 s poll.
    refetchInterval: 60_000,
  });

  /* The two option lists the old filter box read from the database: every
     partner account, and every live extra service. Ten minutes stale is fine —
     a new partner is a rare event and the list is a dropdown. */
  const partners = useQuery({ queryKey: ['catalog', 'partners'], queryFn: () => api.partners(), staleTime: 10 * 60_000 });
  const services = useQuery({ queryKey: ['catalog', 'services'], queryFn: () => api.services(), staleTime: 10 * 60_000 });
  const serviceTitle = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of services.data ?? []) m.set(s.id, s.title);
    return m;
  }, [services.data]);

  const rows = data?.rows ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;

  const goToPage = (p: number) => apply({ page: Math.min(totalPages, Math.max(1, p)) });

  // Paging while scrolled to the bottom otherwise leaves you looking at the
  // last rows of a page whose top you have never seen.
  useEffect(() => {
    wrapRef.current?.scrollTo({ top: 0 });
  }, [filters.page, filters.perPage, filters.sort, filters.dir]);

  const sortBy = (col: ReservationSort) =>
    apply({ sort: col, dir: filters.sort === col && filters.dir === 'asc' ? 'desc' : 'asc' });

  /** A sortable header is a control, so it behaves like one for the keyboard. */
  const sortProps = (col: ReservationSort, extra = '') => ({
    className: `${extra} sortable`.trim(),
    role: 'button' as const,
    tabIndex: 0,
    'aria-sort': (filters.sort === col
      ? filters.dir === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none') as 'ascending' | 'descending' | 'none',
    onClick: () => sortBy(col),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        sortBy(col);
      }
    },
  });

  const arrow = (col: ReservationSort) =>
    filters.sort === col ? (
      <span className="arrow" aria-hidden="true">
        {filters.dir === 'asc' ? '▴' : '▾'}
      </span>
    ) : null;

  /** "vyčistit filtr": back to the legacy default view. */
  const reset = () => {
    settled.current = '';
    setTerm('');
    setSp(toSearchParams(makeDefaults()), { replace: true });
  };

  /**
   * Clicking a row opens the reservation here, under the row, instead of
   * navigating to the detail page.
   *
   * The owner's note, in his words: "My dispatchers are used to opening a
   * reservation from dashboard in drop down window, we can do a screen opened
   * without going to details right away and then having to go back". It costs
   * no request: every field the panel shows is already on the row the grid was
   * handed, and it is the same panel component the dashboard renders.
   */
  const [openKey, setOpenKey] = useState<string | null>(null);
  const toggleRow = rowToggle(openKey, setOpenKey);

  /* An open panel must not survive the list changing underneath it. `params`
     is the whole query, so one dependency covers paging, sorting and every
     filter. */
  useEffect(() => {
    setOpenKey(null);
  }, [params]);

  useEffect(() => {
    if (!openKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenKey(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey]);

  /** Is anything narrowing the grid beyond the legacy default? Drives "Aktivní filtry:". */
  const filtersActive = useMemo(() => {
    const d = makeDefaults();
    return (
      searching ||
      filters.status.length > 0 ||
      filters.type !== '' ||
      filters.needadmin ||
      filters.partnerId !== '' ||
      filters.services.length > 0 ||
      filters.club !== '' ||
      filters.source !== '' ||
      filters.site !== '' ||
      filters.unpaidOnly ||
      filters.missingCarOnly ||
      filters.dateField !== d.dateField ||
      filters.from !== d.from ||
      filters.to !== d.to
    );
  }, [filters, searching]);

  /** Plain language for what is currently narrowing the grid (the empty state). */
  const activeFilters = useMemo(() => {
    const out: string[] = [];
    if (searching) out.push(`hledaný výraz „${filters.q}“`);
    if (filters.from || filters.to) {
      out.push(`${DATEFIELD_LABEL[filters.dateField]} ${filters.from || '…'} – ${filters.to || '…'}`);
    }
    if (filters.status.length) {
      out.push(
        `stav ${filters.status
          .map((s) => STATUS_FILTER_OPTIONS.find((o) => o.value === s)?.label ?? STATUS_LABEL[s] ?? s)
          .join(', ')}`,
      );
    }
    if (filters.type) out.push(`parking ${TYPE_FILTER_LABEL[filters.type] ?? filters.type}`);
    if (filters.needadmin) out.push('vyžaduje pozornost administrátora');
    if (filters.partnerId) out.push(`partner ${partners.data?.find((p) => String(p.id) === filters.partnerId)?.name ?? filters.partnerId}`);
    if (filters.services.length) out.push(`extra služby ${filters.services.map((id) => serviceTitle.get(id) ?? `#${id}`).join(', ')}`);
    if (filters.club) out.push(`Smile Club ${CLUB_FILTER_OPTIONS.find((o) => o.value === filters.club)?.label ?? filters.club}`);
    if (filters.source) out.push(`web ${SOURCE_FILTER_OPTIONS.find((o) => o.value === filters.source)?.label ?? filters.source}`);
    if (filters.site) out.push(`kde stojí ${SITE_FILTER_LABEL[filters.site] ?? filters.site}`);
    if (filters.unpaidOnly) out.push('jen nedoplatky');
    if (filters.missingCarOnly) out.push('jen chybějící auto / SPZ');
    return out;
  }, [filters, searching, partners.data, serviceTitle]);

  const toggleService = (id: number, on: boolean) =>
    apply({ services: on ? [...filters.services, id] : filters.services.filter((x) => x !== id) });

  /* Recomputed per render rather than memoised on a captured `now`: a screen
     left open across midnight would otherwise keep offering yesterday under
     the word "Dnes". */
  const nowRef = new Date();
  const activeQuick = quickRangeKey(filters, nowRef);

  const COLS = 14;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Rezervace</h1>
        <span className="sub recordcount">{data ? `${CZK.format(data.total)} ${zaznamWord(data.total)}` : '…'}</span>
        <div className="acts">
          {/* The legacy grid's "+ Nová rezervace", where it was. */}
          <Link className="btn primary" to="/rezervace/nova">
            + Nová rezervace
          </Link>
          <form onSubmit={(e) => e.preventDefault()} role="search">
            <label className="visually-hidden" htmlFor="q">
              Hledat
            </label>
            <input
              id="q"
              type="search"
              placeholder="Hledat"
              title="Hledá v ID, jménu, e-mailu, telefonu, poznámce, promokódu, autu, SPZ, letu, fakturačních údajích a názvu ceníku — každé slovo musí být nalezeno"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </form>
        </div>
      </div>

      {/* The legacy filter box (inc.filterbox.html + inc.daterange.html +
          the page's own three rows), in its order: Datum with the
          rezervace / příjezd / odjezd selector; Stav, Parking, Vyžaduje
          pozornost; Smile Club, Partner, Extra služby; Web (zdroj). Ours,
          after them: Kde stojí, Jen nedoplatky, Chybí auto / SPZ. */}
      <div className="card toolbar">
        <div className="filterhead">
          {filtersActive ? (
            <>
              <b>Aktivní filtry:</b>
              <button className="btn sm danger" type="button" onClick={reset}>
                ✕ vyčistit filtr
              </button>
            </>
          ) : (
            <span style={{ color: 'var(--ink-3)' }}>Posledních {3} měsíce podle data rezervace</span>
          )}
        </div>

        <label className="lbl" htmlFor="f_from">
          Datum:
        </label>
        <input
          id="f_from"
          type="date"
          value={filters.from}
          onChange={(e) => apply({ from: e.target.value })}
          aria-label="Datum od"
        />
        <span style={{ color: 'var(--ink-3)' }}>–</span>
        <input
          type="date"
          value={filters.to}
          onChange={(e) => apply({ to: e.target.value })}
          aria-label="Datum do"
        />
        {/* The selector beside Datum: which date the range reads. The old
            page's default is "rezervace" — the day the booking was made. */}
        <select
          value={filters.dateField}
          onChange={(e) => apply({ dateField: e.target.value as DateField })}
          aria-label="Které datum"
          title="Podle kterého data se rozsah vybírá"
        >
          {DATE_FIELDS.map((f) => (
            <option key={f} value={f}>
              {DATEFIELD_LABEL[f]}
            </option>
          ))}
        </select>

        <div className="quickrange" role="group" aria-label="Rychlý výběr období">
          {QUICK_RANGES.map((q) => (
            <button
              key={q.key}
              type="button"
              className={activeQuick === q.key ? 'qbtn on' : 'qbtn'}
              title={q.title}
              aria-pressed={activeQuick === q.key}
              onClick={() => apply(q.range(new Date()))}
            >
              {q.label}
            </button>
          ))}
        </div>

        <span className="sep" />

        <label className="lbl" htmlFor="f_status">
          Stav:
        </label>
        <select
          id="f_status"
          value={filters.status[0] ?? ''}
          onChange={(e) => apply({ status: e.target.value ? [e.target.value] : [] })}
          aria-label="Stav"
        >
          <option value="">&nbsp;</option>
          {STATUS_FILTER_OPTIONS.filter((o) => o.group).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <optgroup label="Pouze">
            {STATUS_FILTER_OPTIONS.filter((o) => !o.group).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        </select>

        <label className="lbl" htmlFor="f_type">
          Parking:
        </label>
        <select
          id="f_type"
          value={filters.type}
          onChange={(e) => apply({ type: e.target.value })}
          aria-label="Parking"
        >
          <option value="">&nbsp;</option>
          {RESERVATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_FILTER_LABEL[t]}
            </option>
          ))}
        </select>

        <label className="chk" title="Vyžaduje pozornost administrátora!">
          <input
            type="checkbox"
            checked={filters.needadmin}
            onChange={(e) => apply({ needadmin: e.target.checked })}
          />
          ⚠ Vyžaduje pozornost administrátora!
        </label>

        <span className="sep" />

        <label className="lbl" htmlFor="f_club">
          Smile Club:
        </label>
        <select id="f_club" value={filters.club} onChange={(e) => apply({ club: e.target.value })} aria-label="Smile Club">
          <option value="">&nbsp;</option>
          {CLUB_FILTER_OPTIONS.filter((o) => o.group).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <optgroup label="Pouze:">
            {CLUB_FILTER_OPTIONS.filter((o) => !o.group).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        </select>

        <label className="lbl" htmlFor="f_partner">
          Partner:
        </label>
        <select
          id="f_partner"
          value={filters.partnerId}
          onChange={(e) => apply({ partnerId: e.target.value })}
          aria-label="Partner"
        >
          <option value="">&nbsp;</option>
          {(partners.data ?? []).map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>

        <span className="lbl">Extra služby:</span>
        <span className="services" role="group" aria-label="Extra služby">
          {(services.data ?? []).map((s) => {
            return (
              <label key={s.id} className="chk" title={s.title}>
                <input
                  type="checkbox"
                  checked={filters.services.includes(s.id)}
                  onChange={(e) => toggleService(s.id, e.target.checked)}
                />
                <span aria-hidden="true"><ServiceIcon id={s.id} title={s.title} /></span>
                <span className="visually-hidden">{s.title}</span>
              </label>
            );
          })}
        </span>

        <span className="sep" />

        <label className="lbl" htmlFor="f_source">
          Web (zdroj):
        </label>
        <select
          id="f_source"
          value={filters.source}
          onChange={(e) => apply({ source: e.target.value })}
          aria-label="Web (zdroj)"
        >
          <option value="">&nbsp;</option>
          {SOURCE_FILTER_OPTIONS.filter((o) => o.group).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <optgroup label="Pouze:">
            {SOURCE_FILTER_OPTIONS.filter((o) => !o.group).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        </select>

        <span className="sep" />

        {/* Ours: the yard the car stands in (the owner's "the filters from and
            where … do not exist"), the debt filter, the missing-car filter. */}
        <select
          value={filters.site}
          onChange={(e) => apply({ site: e.target.value })}
          aria-label="Kde auto stojí"
          title="Parkoviště, na kterém auto stojí"
        >
          <option value="">Kde stojí: vše</option>
          {SITE_FILTERS.map((s) => (
            <option key={s} value={s}>
              {SITE_FILTER_LABEL[s]}
            </option>
          ))}
        </select>
        <label className="chk">
          <input
            type="checkbox"
            checked={filters.unpaidOnly}
            onChange={(e) => apply({ unpaidOnly: e.target.checked })}
          />
          Jen nedoplatky
        </label>
        <label className="chk">
          <input
            type="checkbox"
            checked={filters.missingCarOnly}
            onChange={(e) => apply({ missingCarOnly: e.target.checked })}
          />
          Chybí auto / SPZ
        </label>
      </div>

      <div className="card tablecard" style={{ marginTop: 14 }}>
        <div className="tablewrap" ref={wrapRef}>
          <table className="legacygrid">
            <thead>
              <tr>
                <th {...sortProps('id', 'num')}>ID {arrow('id')}</th>
                <th {...sortProps('status')}>Stav {arrow('status')}</th>
                <th {...sortProps('driverName')}>Klient {arrow('driverName')}</th>
                <th {...sortProps('partner')}>Partner {arrow('partner')}</th>
                <th {...sortProps('pricelist')}>Ceník {arrow('pricelist')}</th>
                <th {...sortProps('type')}>Kde {arrow('type')}</th>
                <th {...sortProps('arrival', 'num')}>Příjezd {arrow('arrival')}</th>
                <th {...sortProps('departure', 'num')}>Odjezd {arrow('departure')}</th>
                <th {...sortProps('days', 'num')}>Dní {arrow('days')}</th>
                <th {...sortProps('created', 'num')}>Rezervováno {arrow('created')}</th>
                <th {...sortProps('total', 'num')}>Cena {arrow('total')}</th>
                <th {...sortProps('gopay', 'num')}>GoPay {arrow('gopay')}</th>
                <th {...sortProps('invoice', 'num')}>Fakturace CK {arrow('invoice')}</th>
                <th {...sortProps('topay', 'num')}>Uhradit {arrow('topay')}</th>
              </tr>
            </thead>

            <tbody>
              {isPending &&
                Array.from({ length: 12 }, (_, i) => (
                  <tr key={`sk${i}`}>
                    {Array.from({ length: COLS }, (_, j) => (
                      <td key={j}>
                        <div className="skeleton" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isPending && rows.length === 0 && (
                <tr>
                  <td colSpan={COLS} className="state">
                    <p>
                      <b>Žádné rezervace neodpovídají filtru.</b>
                    </p>
                    {activeFilters.length > 0 && (
                      <p style={{ marginTop: 6 }}>Aktivní filtry: {activeFilters.join(' · ')}</p>
                    )}
                    <button className="btn sm" type="button" onClick={reset} style={{ marginTop: 12 }}>
                      vyčistit filtr
                    </button>
                  </td>
                </tr>
              )}

              {rows.map((r) => {
                const arr = splitFull(r.arrivalReal ?? r.arrival);
                const dep = splitFull(r.departureReal ?? r.departure);
                const cre = splitFull(r.created);
                const k = String(r.id);
                const clientName = r.driverName || r.driverEmail || r.driverPhone;
                const rp = toggleRow(r, k);
                return (
                  <Fragment key={k}>
                    <tr
                      {...rp}
                      className={`click${openKey === k ? ' open' : ''} ${legacyRowClass(r)}`.trim()}
                      title={r.reslock ? 'Uzamčená rezervace, již není možné měnit.' : undefined}
                    >
                      <td className="num">
                        {r.status === 'closed' && (
                          <Link
                            className="btn mini"
                            to={`/uctenka/${r.id}`}
                            target="_blank"
                            title="Tisk účtenky"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🖨
                          </Link>
                        )}{' '}
                        {r.reslock && (
                          <span aria-hidden="true" title="Uzamčená rezervace">
                            🔒
                          </span>
                        )}{' '}
                        <small>{r.id}</small>
                      </td>
                      <td>
                        <span className={STATUS_CLASS[r.status] ?? 'stav gray'}>
                          {STATUS_LABEL[r.status] ?? r.status}
                          <span className="caret2" aria-hidden="true">
                            ▾
                          </span>
                        </span>
                        {r.status !== 'paid' && r.paidGopay > 0 && (
                          <span title={`${CZK.format(r.paidGopay)} Kč již zaplaceno přes GoPay`}> +◎</span>
                        )}
                        {r.status !== 'paid' && r.paidGopay === 0 && r.paidBitpay > 0 && (
                          <span title={`${CZK.format(r.paidBitpay)} Kč již zaplaceno přes BitPay`}> +◎</span>
                        )}
                        {r.status === 'closed' && r.payment === 'cash' && <span title="Placeno hotově"> 💵</span>}
                        {r.status === 'closed' && r.payment === 'card' && <span title="Placeno kartou"> 💳</span>}
                      </td>
                      <td className="klient">
                        {r.clubUserId != null && (
                          <span className={`flag ${r.clubTier === 'platinum' ? 'plat' : r.clubTier === 'gold' ? 'gold' : 'silver'}`} title={r.clubActive ? `Smile Parking Club: ${r.clubTier ?? ''}` : 'Dosud neaktivní Člen Smile Parking Clubu'}>
                            {r.clubActive ? (r.clubTier ?? 'club').toUpperCase() : '👤'}
                          </span>
                        )}{' '}
                        <b className="name">{clientName || <em>nezadáno</em>}</b>{' '}
                        {r.language && <small className="lang" title={`Jazyk: ${r.language}`}>{LANGUAGE_FLAG[r.language] ?? r.language}</small>}
                        <span className="icons">
                          {r.serviceIds.map((id) => (
                            <ServiceIcon key={id} id={id} title={serviceTitle.get(id)} />
                          ))}
                          {r.note && <span title={`Poznámka: ${r.note}`}>🚩</span>}
                          {r.carkey && <span title="Klíče od vozidla máme.">🏷</span>}
                          {r.called && <span title="Klient telefonicky ověřen">☎</span>}
                          {r.needadmin && <span title="Vyžaduje pozornost administrátora!">⚠</span>}
                        </span>
                      </td>
                      <td>
                        {r.source && r.source !== 'smileparking' && (
                          <span className="badge-src">{SOURCE_LABEL[r.source] ?? r.source}</span>
                        )}{' '}
                        <small>{r.partnerName ?? ''}</small>
                      </td>
                      <td>
                        <small>{r.pricelistTitle}</small>
                      </td>
                      <td title={TYPE_FILTER_LABEL[r.type] ?? r.type}>
                        <span className="typ">
                          {r.type === 'exclusive' ? '★ ' : r.type === 'cheap' ? '🅐 ' : '🏠 '}
                          {TYPE_SHORT[r.type] ?? r.type}
                        </span>
                      </td>
                      <td className="time num">
                        {arr.d}
                        <small>{arr.t}</small>
                      </td>
                      <td className="time num">
                        {dep.d}
                        <small>{dep.t}</small>
                      </td>
                      <td className="num">{r.days}</td>
                      <td className="time num">
                        {cre.d}
                        <small>{cre.t}</small>
                      </td>
                      <td className="num">
                        <b>{CZK.format(r.total)}&nbsp;Kč</b>
                        {r.penalty > 0 && (
                          <span
                            className="badge-pen"
                            title={`Zpoždění, k základní ceně je naúčtováno navíc ${CZK.format(r.penalty)} Kč za zpoždění!`}
                          >
                            vč.&nbsp;{CZK.format(r.penalty)}&nbsp;Kč
                          </span>
                        )}
                      </td>
                      <td className="num">
                        {r.paidGopay > 0 && r.gopayState === 'PAID' ? (
                          <b>{CZK.format(r.paidGopay)}&nbsp;Kč</b>
                        ) : r.gopayState ? (
                          <small>{r.gopayState}</small>
                        ) : r.gopaySession ? (
                          'čeká'
                        ) : (
                          ''
                        )}
                      </td>
                      <td className="num">{r.paidInvoice > 0 ? `${CZK.format(r.paidInvoice)} Kč` : ''}</td>
                      <td className="num">
                        {r.outstanding > 0 ? <span className="badge-pen">{CZK.format(r.outstanding)}&nbsp;Kč</span> : ''}
                      </td>
                    </tr>
                    {openKey === k && (
                      <tr className="drawerrow">
                        <td colSpan={COLS}>
                          <Drawer r={r} onClose={() => setOpenKey(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>

            {/* The legacy footer (getGroupTotal): over the whole filtered set,
                not the page. Dní is the AVERAGE, as the old page printed it. */}
            {data && (
              <tfoot>
                <tr className="pagetotal">
                  <td colSpan={8}>
                    Celkem: {CZK.format(data.total)} {rezWord(data.total)}
                  </td>
                  <td className="num" title="Průměrný počet dní">
                    {CZK.format(Math.round(data.totals.avgDays))}
                  </td>
                  <td />
                  {data.totalsHidden ? (
                    <td colSpan={4} />
                  ) : (
                    <>
                      <td className="num">{CZK.format(data.totals.total)}&nbsp;Kč</td>
                      <td className="num">{CZK.format(data.totals.gopay)}&nbsp;Kč</td>
                      <td className="num">{CZK.format(data.totals.invoice)}&nbsp;Kč</td>
                      <td className="num">{CZK.format(data.totals.topay)}&nbsp;Kč</td>
                    </>
                  )}
                </tr>
                {/* Codex F-01: company money sums only for admins; the API zeroes them for everyone else. */}
                {!data.totalsHidden && (
                <>
                <tr>
                  <td colSpan={10} className="num">
                    Placeno hotově:
                  </td>
                  <td className="num">{CZK.format(data.totals.paidCash)}&nbsp;Kč</td>
                  <td colSpan={3} />
                </tr>
                <tr>
                  <td colSpan={10} className="num">
                    Placeno kartou:
                  </td>
                  <td className="num">{CZK.format(data.totals.paidCard)}&nbsp;Kč</td>
                  <td colSpan={3} />
                </tr>
                </>
                )}
              </tfoot>
            )}
          </table>
        </div>

        {isError && (
          <div className="state" role="alert">
            Data se nepodařilo načíst
            {error instanceof Error && error.message ? ` (${error.message})` : ''}.{' '}
            <button className="btn sm" type="button" onClick={() => refetch()}>
              Zkusit znovu
            </button>
          </div>
        )}

        <div className="pager">
          <span>
            {data
              ? `Strana ${data.page} z ${CZK.format(totalPages)} · ${CZK.format(data.total)} ${zaznamWord(data.total)}`
              : '…'}
            {isFetching && !isPending ? ' · aktualizuji' : ''}
          </span>
          <select
            value={filters.perPage}
            onChange={(e) => apply({ perPage: Number(e.target.value) })}
            style={{ marginLeft: 12 }}
            aria-label="Počet řádků na stranu"
          >
            {[20, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n} / strana
              </option>
            ))}
          </select>

          {totalPages > 10 && (
            <label className="jump">
              Strana
              <input
                type="number"
                min={1}
                max={totalPages}
                key={filters.page}
                defaultValue={filters.page}
                aria-label="Přejít na stranu"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    goToPage(Number((e.target as HTMLInputElement).value));
                  }
                }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (n && n !== filters.page) goToPage(n);
                }}
              />
            </label>
          )}

          <div className="pages">
            <button onClick={() => goToPage(filters.page - 1)} disabled={filters.page <= 1} aria-label="Předchozí strana">
              « předchozí
            </button>
            {pageList(filters.page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap${i}`} className="gap">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={p === filters.page ? 'on' : undefined}
                  aria-current={p === filters.page ? 'page' : undefined}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => goToPage(filters.page + 1)}
              disabled={filters.page >= totalPages}
              aria-label="Další strana"
            >
              další »
            </button>
          </div>
        </div>
      </div>

      {/* inc.iconlegend.html, the part that applies to this grid. */}
      <details className="iconlegend">
        <summary>Legenda</summary>
        <p>
          <b>Stavy rezervací:</b> Nová rezervace, čeká na kontrolu · Předplacená rezervace dopředu přes online platbu GoPay ·
          Aktivní (parkuje) na parkovišti, čeká na odjezd · Uzavřená rezervace, klient vyrovnal závazky a odjel · Propadlá
          rezervace, klient nepřijel · Zrušená rezervace, klient stornoval · Smazaná rezervace, jednalo se o test, duplicitu, atd.
        </p>
        <p>
          <b>Typ parkování:</b> 🅐 Venkovní · 🏠 Garážové · ★ Garážové VIP
        </p>
        <p>
          <b>Extra služby:</b> ✈ Valet parking! · 🧹 Čištění interiéru vozidla · ⛽ Dotankování vozidla · 💧 Ruční venkovní mytí ·
          🧽 Tepování vozidla
        </p>
        <p>
          <b>Ikonky:</b> 🖨 Vytisknout účtenku · 🚩 Poznámka, zobrazíte podržením myši · 🏷 Klíče od vozidla máme · ☎ Klient telefonicky ověřen · ⚠ Vyžaduje
          pozornost administrátora! · 🔒 Uzamčená rezervace · 👤 Dosud neaktivní člen Smile Parking Clubu
        </p>
      </details>

      <Footer />
    </div>
  );
}
